import * as Contacts from 'expo-contacts';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  ChevronLeft,
  ContactRound,
  Mail,
  Phone,
  Plus,
  Search,
  UserCircle2,
  X,
} from 'lucide-react-native';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { InfiniteFooter } from '@/components/InfiniteFooter';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { useInfiniteWindow } from '@/hooks/use-infinite-window';
import { addContact, listContacts, markContactUsed } from '@/lib/contacts';
import { goBackOrTo } from '@/lib/nav';
import { getActiveProperty } from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';
import type { ContactRow } from '@/db/types';

interface DeviceContact {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  company: string | null;
}

export default function ContactsList() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const [importerOpen, setImporterOpen] = useState(false);
  const [deviceContacts, setDeviceContacts] = useState<DeviceContact[]>([]);
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSearch, setImportSearch] = useState('');

  const fetch = useCallback(async () => {
    const prop = await getActiveProperty(db);
    if (!prop) return;
    setPropertyId(prop.id);
    setContacts(await listContacts(db, prop.id));
  }, [db]);

  // Window the saved contacts list. Most users won't have hundreds of
  // pros saved, but anyone who imports their phone's contacts will
  // suddenly land here with a wall of rows — pre-windowing makes that
  // experience scroll smoothly on Android instead of choking on render.
  const contactsWin = useInfiniteWindow(contacts, { pageSize: 25, initialSize: 20 });

  // Wave 62: /contacts?import=1 auto-opens the device importer — the
  // Add-contact chooser hands off here for "Import from my phone."
  const params = useLocalSearchParams<{ import?: string }>();
  const autoImportRef = useRef(false);
  useEffect(() => {
    if (params.import === '1' && !autoImportRef.current) {
      autoImportRef.current = true;
      void openImporter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.import]);

  const openImporter = async () => {
    setImporterOpen(true);
    setImportError(null);
    if (Platform.OS === 'web') {
      // Browsers don't expose a useful contacts API across browsers; tell
      // the user directly instead of failing silently.
      setImportError(
        "Importing from device contacts isn't supported in the browser. Open Homestead on iOS or Android to use this."
      );
      setDeviceContacts([]);
      return;
    }
    setImportBusy(true);
    try {
      const { status } = await Contacts.requestPermissionsAsync();
      if (status !== 'granted') {
        setImportError('Permission denied. Enable Contacts access in your device Settings.');
        return;
      }
      const { data } = await Contacts.getContactsAsync({
        fields: [
          Contacts.Fields.PhoneNumbers,
          Contacts.Fields.Emails,
          Contacts.Fields.Company,
          Contacts.Fields.Name,
        ],
        sort: Contacts.SortTypes.FirstName,
      });
      const normalized: DeviceContact[] = data
        .filter((c) => c.name && (c.phoneNumbers?.length || c.emails?.length))
        .map((c) => ({
          id: c.id ?? '',
          name: c.name ?? '',
          phone: c.phoneNumbers?.[0]?.number ?? null,
          email: c.emails?.[0]?.email ?? null,
          company: c.company ?? null,
        }));
      setDeviceContacts(normalized);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Failed to read contacts');
    } finally {
      setImportBusy(false);
    }
  };

  const importOne = async (c: DeviceContact) => {
    if (!propertyId) return;
    await addContact(db, {
      propertyId,
      name: c.name,
      phone: c.phone,
      email: c.email,
      company: c.company,
      role: null,
    });
    await fetch();
    setImporterOpen(false);
  };

  const filteredDevice = useMemo(() => {
    const q = importSearch.trim().toLowerCase();
    if (!q) return deviceContacts;
    return deviceContacts.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.company ?? '').toLowerCase().includes(q) ||
        (c.phone ?? '').toLowerCase().includes(q)
    );
  }, [deviceContacts, importSearch]);

  useFocusEffect(
    useCallback(() => {
      fetch();
    }, [fetch])
  );

  const callPhone = async (c: ContactRow) => {
    if (!c.phone) return;
    await markContactUsed(db, c.id);
    Linking.openURL(`tel:${c.phone.replace(/[^0-9+]/g, '')}`).catch(() => {});
  };
  const emailContact = async (c: ContactRow) => {
    if (!c.email) return;
    await markContactUsed(db, c.id);
    Linking.openURL(`mailto:${c.email}`).catch(() => {});
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/(tabs)/my-home')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Contacts</Text>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          <Pressable
            onPress={openImporter}
            style={({ pressed }) => [styles.import, pressed && { opacity: 0.85 }]}>
            <ContactRound size={16} color={Palette.forest} strokeWidth={2} />
          </Pressable>
          <Pressable
            onPress={() => router.push('/contact/new' as never)}
            style={({ pressed }) => [styles.add, pressed && { opacity: 0.85 }]}>
            <Plus size={18} color={Palette.cream} strokeWidth={2.2} />
          </Pressable>
        </View>
      </View>

      <ScrollView
        contentContainerStyle={{ paddingBottom: 60 }}
        onScroll={contactsWin.onScroll}
        onLayout={contactsWin.onLayout}
        scrollEventThrottle={16}>
        <Text style={styles.lead}>
          Your trusted pros, ready when you need them. Tap to call or email.
        </Text>

        {contacts.length === 0 ? (
          <View style={styles.empty}>
            <UserCircle2 size={42} color={Palette.inkFaint} strokeWidth={1.4} />
            <Text style={styles.emptyTitle}>No contacts yet</Text>
            <Text style={styles.emptyBody}>
              Add your plumber, HVAC tech, electrician, or any pro you've used —
              one tap saves them.
            </Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <Pressable
                onPress={() => router.push('/contact/new' as never)}
                style={({ pressed }) => [styles.emptyCta, pressed && { transform: [{ scale: 0.98 }] }]}>
                <Plus size={16} color={Palette.cream} strokeWidth={2} />
                <Text style={styles.emptyCtaText}>New contact</Text>
              </Pressable>
              <Pressable
                onPress={openImporter}
                style={({ pressed }) => [styles.emptyCtaSecondary, pressed && { transform: [{ scale: 0.98 }] }]}>
                <ContactRound size={16} color={Palette.forest} strokeWidth={2} />
                <Text style={styles.emptyCtaSecondaryText}>Import from device</Text>
              </Pressable>
            </View>
          </View>
        ) : (
          <View style={styles.list}>
            {contactsWin.visible.map((c) => (
              <Pressable
                key={c.id}
                accessibilityRole="button"
                onPress={() => router.push(`/contact/${c.id}` as never)}
                style={({ pressed }) => [styles.row, pressed && { transform: [{ scale: 0.99 }] }]}>
                <View style={styles.avatar}>
                  <Text style={styles.avatarText}>{initials(c.name)}</Text>
                </View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.name}>{c.name}</Text>
                  <Text style={styles.meta} numberOfLines={1}>
                    {[c.role, c.company].filter(Boolean).join(' · ') || 'No role set'}
                  </Text>
                  {c.phone || c.email ? (
                    <Text style={styles.contact} numberOfLines={1}>
                      {[c.phone, c.email].filter(Boolean).join(' · ')}
                    </Text>
                  ) : null}
                </View>
                {c.phone ? (
                  <Pressable
                    onPress={() => callPhone(c)}
                    hitSlop={8}
                    style={({ pressed }) => [styles.iconBtn, pressed && { opacity: 0.7 }]}>
                    <Phone size={18} color={Palette.forest} strokeWidth={1.8} />
                  </Pressable>
                ) : null}
                {c.email ? (
                  <Pressable
                    onPress={() => emailContact(c)}
                    hitSlop={8}
                    style={({ pressed }) => [styles.iconBtn, pressed && { opacity: 0.7 }]}>
                    <Mail size={18} color={Palette.forest} strokeWidth={1.8} />
                  </Pressable>
                ) : null}
              </Pressable>
            ))}
            <InfiniteFooter hasMore={contactsWin.hasMore} total={contactsWin.total} loadMore={contactsWin.loadMore} noun="contact" />
          </View>
        )}
      </ScrollView>

      {/* Device contacts importer */}
      <Modal animationType="fade" transparent visible={importerOpen} onRequestClose={() => setImporterOpen(false)}>
        <Pressable style={styles.backdrop} onPress={() => setImporterOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <View style={styles.modalHead}>
              <Text style={styles.modalTitle}>Import from device</Text>
              <Pressable onPress={() => setImporterOpen(false)} hitSlop={8}>
                <X size={18} color={Palette.inkSoft} strokeWidth={1.8} />
              </Pressable>
            </View>
            {importError ? (
              <View style={styles.errorBox}>
                <Text style={styles.errorText}>{importError}</Text>
              </View>
            ) : null}
            {importBusy ? (
              <Text style={styles.modalEmpty}>Loading your contacts…</Text>
            ) : !importError ? (
              <>
                <View style={styles.searchRow}>
                  <Search size={14} color={Palette.inkFaint} strokeWidth={1.8} />
                  <TextInput
                    value={importSearch}
                    onChangeText={setImportSearch}
                    placeholder="Search by name, company, phone…"
                    placeholderTextColor={Palette.inkFaint}
                    style={[styles.searchInput, webNoOutline]}
                  />
                </View>
                <ScrollView style={{ maxHeight: 380 }}>
                  {filteredDevice.length === 0 ? (
                    <Text style={styles.modalEmpty}>
                      No contacts with phone or email found.
                    </Text>
                  ) : (
                    filteredDevice.map((c) => (
                      <Pressable
                        key={c.id}
                        onPress={() => importOne(c)}
                        style={({ pressed }) => [
                          styles.deviceRow,
                          pressed && { backgroundColor: '#EEF3EE' },
                        ]}>
                        <View style={styles.deviceAvatar}>
                          <Text style={styles.deviceAvatarText}>{initials(c.name)}</Text>
                        </View>
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <Text style={styles.deviceName}>{c.name}</Text>
                          <Text style={styles.deviceMeta} numberOfLines={1}>
                            {[c.company, c.phone, c.email].filter(Boolean).join(' · ')}
                          </Text>
                        </View>
                        <Plus size={16} color={Palette.forest} strokeWidth={2} />
                      </Pressable>
                    ))
                  )}
                </ScrollView>
              </>
            ) : null}
          </Pressable>
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.two,
    paddingBottom: Spacing.two,
  },
  back: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  add: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: Palette.forest,
    alignItems: 'center',
    justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  lead: {
    paddingHorizontal: Spacing.four,
    marginTop: Spacing.three,
    fontSize: 13,
    color: Palette.inkSoft,
    fontFamily: Fonts.sansMedium,
  },
  empty: {
    alignItems: 'center',
    paddingHorizontal: 32,
    paddingVertical: 56,
    gap: 12,
  },
  emptyTitle: {
    fontFamily: Fonts.serifBold,
    fontSize: 19,
    color: Palette.ink,
  },
  emptyBody: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    textAlign: 'center',
    lineHeight: 20,
  },
  emptyCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Palette.forest,
    paddingHorizontal: 18,
    paddingVertical: 12,
    borderRadius: 14,
    marginTop: 8,
  },
  emptyCtaText: {
    color: Palette.cream,
    fontFamily: Fonts.sansBold,
    fontSize: 14,
  },
  list: {
    paddingHorizontal: 18,
    marginTop: Spacing.three,
    gap: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    padding: 12,
  },
  avatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: '#DCE7E1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.forest,
  },
  name: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.ink,
  },
  meta: {
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
    marginTop: 1,
  },
  contact: {
    fontFamily: Fonts.sansMedium,
    fontSize: 12,
    color: Palette.inkFaint,
    marginTop: 1,
  },
  iconBtn: {
    width: 36,
    height: 36,
    borderRadius: 12,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  import: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyCtaSecondary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 14,
  },
  emptyCtaSecondaryText: {
    color: Palette.forest,
    fontFamily: Fonts.sansBold,
    fontSize: 13,
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modalCard: {
    backgroundColor: Palette.sand,
    borderRadius: 24,
    padding: 18,
    width: '100%',
    maxWidth: 420,
  },
  modalHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  modalTitle: { fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink },
  modalEmpty: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    paddingVertical: 16,
    textAlign: 'center',
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    borderRadius: 11,
    paddingHorizontal: 10,
    marginBottom: 8,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 9,
    fontFamily: Fonts.sansSemibold,
    fontSize: 13,
    color: Palette.ink,
  },
  deviceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  deviceAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#DCE7E1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deviceAvatarText: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.forest },
  deviceName: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  deviceMeta: {
    fontFamily: Fonts.sansMedium,
    fontSize: 12,
    color: Palette.inkSoft,
    marginTop: 1,
  },
  errorBox: {
    backgroundColor: 'rgba(176,95,60,0.12)',
    borderRadius: 10,
    padding: 11,
    marginBottom: 8,
  },
  errorText: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13,
    color: Palette.clay,
    lineHeight: 17,
  },
});
